import React, { useCallback, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import CampaignIcon from '@mui/icons-material/Campaign';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';

import { createDefaultState, withDefaultSuccessSteps } from '~/utils/societePayloadBuilder/defaults';
import { hasFormProgress } from '~/utils/societePayloadBuilder/hasFormProgress';
import { parsePayloadJson } from '~/utils/societePayloadBuilder/parsePayload';
import type { CampaignMode, PayloadBuilderState } from '~/utils/societePayloadBuilder/types';

import PayloadBuilderExportActions from './PayloadBuilder/PayloadBuilderExportActions';
import PayloadBuilderForm from './PayloadBuilder/PayloadBuilderForm';
import PayloadBuilderPreview from './PayloadBuilder/PayloadBuilderPreview';
import PayloadBuilderSetupGuide from './PayloadBuilder/PayloadBuilderSetupGuide';
import { PAYLOAD_BUILDER_ALERT_BUTTON_SX } from './PayloadBuilder/PayloadBuilderShared';
import PayloadBuilderToolbar from './PayloadBuilder/PayloadBuilderToolbar';

const WORKSPACE_MAX_HEIGHT = 'calc(100vh - 300px)';

const MODE_LABELS: Record<CampaignMode, string> = {
  statusMatch: 'Status Match',
  statusClaim: 'Status Claim',
};

export default function LoyaltyPayloadBuilderPage() {
  const theme = useTheme();
  const borderSoft = alpha(theme.palette.divider, 0.12);

  const [state, setState] = useState<PayloadBuilderState>(() => createDefaultState('statusMatch'));
  const [loadJson, setLoadJson] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingMode, setPendingMode] = useState<CampaignMode | null>(null);
  const [isModeConfirmOpen, setIsModeConfirmOpen] = useState(false);

  const applyModeChange = useCallback((mode: CampaignMode) => {
    const next = createDefaultState(mode);
    setState(mode === 'statusMatch' ? withDefaultSuccessSteps(next) : next);
    setLoadJson('');
    setLoadError(null);
  }, []);

  const handleRequestModeChange = useCallback(
    (mode: CampaignMode) => {
      if (mode === state.mode) return;
      if (hasFormProgress(state, loadJson)) {
        setPendingMode(mode);
        setIsModeConfirmOpen(true);
        return;
      }
      applyModeChange(mode);
    },
    [applyModeChange, loadJson, state],
  );

  const handleConfirmModeChange = useCallback(() => {
    if (pendingMode) {
      applyModeChange(pendingMode);
    }
    setPendingMode(null);
    setIsModeConfirmOpen(false);
  }, [applyModeChange, pendingMode]);

  const handleCancelModeChange = useCallback(() => {
    setPendingMode(null);
    setIsModeConfirmOpen(false);
  }, []);

  const applyJson = useCallback((text: string) => {
    try {
      setState(parsePayloadJson(text));
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  }, []);

  const handleLoadPayload = useCallback(() => {
    applyJson(loadJson);
  }, [applyJson, loadJson]);

  const handleUploadFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        setLoadJson(text);
        applyJson(text);
      };
      reader.readAsText(file);
    },
    [applyJson],
  );

  const panelSx = {
    borderRadius: 2,
    border: `1px solid ${borderSoft}`,
    maxHeight: WORKSPACE_MAX_HEIGHT,
    overflow: 'auto',
    boxSizing: 'border-box',
  } as const;

  return (
    <Box>
      <Alert icon={<CampaignIcon />} severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Campaign payload builder</AlertTitle>
        <Box>
          Build JSON configs for Société Status Match or Status Claim Lab feature flags. Use the live preview to copy or
          download the payload, then paste it into the Lab flag configuration.
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.5 }}
        >
          <PayloadBuilderSetupGuide />
          <Button
            component={Link}
            to="/lab"
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            color="info"
            startIcon={<OpenInNewIcon fontSize="small" />}
            sx={PAYLOAD_BUILDER_ALERT_BUTTON_SX}
          >
            Open Lab
          </Button>
        </Stack>
      </Alert>

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          mb: 2,
          bgcolor: 'background.default',
        }}
      >
        <PayloadBuilderToolbar state={state} onRequestModeChange={handleRequestModeChange} />
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5.4 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
            <PayloadBuilderForm
              state={state}
              onChange={setState}
              loadJson={loadJson}
              onLoadJsonChange={setLoadJson}
              loadError={loadError}
              onLoadPayload={handleLoadPayload}
              onClearLoad={() => {
                setLoadJson('');
                setLoadError(null);
              }}
              onUploadFile={handleUploadFile}
            />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6.6 }}>
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              mb: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${borderSoft}`,
              bgcolor: 'background.paper',
            }}
          >
            <PayloadBuilderExportActions state={state} fullWidth />
          </Box>
          <PayloadBuilderPreview state={state} borderSoft={borderSoft} maxHeight={WORKSPACE_MAX_HEIGHT} />
        </Grid>
      </Grid>

      <Dialog open={isModeConfirmOpen} onClose={handleCancelModeChange} maxWidth="xs" fullWidth>
        <DialogTitle>Switch payload type?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Switching from {MODE_LABELS[state.mode]} to {pendingMode ? MODE_LABELS[pendingMode] : 'another type'} will
            clear all fields you have entered. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelModeChange}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmModeChange}>
            Switch payload type
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
